(() => {
  const countdownTimers = new WeakMap();
  const duration = {
    day: 24 * 60 * 60 * 1000,
    hour: 60 * 60 * 1000,
    minute: 60 * 1000,
    second: 1000,
  };

  const pad = (value) => String(Math.max(0, value)).padStart(2, '0');

  const parseDate = (value) => {
    if (!value) return null;

    const parsedDate = new Date(value);
    if (!Number.isNaN(parsedDate.getTime())) return parsedDate;

    const normalizedDate = new Date(value.replace(' ', 'T'));
    return Number.isNaN(normalizedDate.getTime()) ? null : normalizedDate;
  };

  const getNumberSetting = (value, fallback) => {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : fallback;
  };

  const getRandomLinks = (banner) =>
    (banner.dataset.countdownRandomLinks || '')
      .split(/\s+/)
      .map((link) => link.trim())
      .filter(Boolean);

  const setRandomLink = (banner) => {
    const linkElement = banner.querySelector('a.countdown-banner__inner');
    if (!linkElement) return;

    const randomLinks = getRandomLinks(banner);
    if (!randomLinks.length) {
      if (banner.dataset.countdownLink) linkElement.href = banner.dataset.countdownLink;
      return;
    }

    const setNextLink = () => {
      linkElement.href = randomLinks[Math.floor(Math.random() * randomLinks.length)];
    };

    setNextLink();

    if (linkElement.dataset.randomCountdownReady === 'true') return;

    linkElement.dataset.randomCountdownReady = 'true';
    linkElement.addEventListener('click', setNextLink);
  };

  const getCountdownTarget = (banner) => {
    if (banner.dataset.countdownSchedule !== 'rolling') {
      const endDate = parseDate(banner.dataset.countdownEnd);
      return endDate ? { status: 'active', endDate } : { status: 'expired' };
    }

    const startDate = parseDate(banner.dataset.countdownStart);
    if (!startDate) return { status: 'expired' };

    const roundHours = getNumberSetting(banner.dataset.countdownRoundHours, 24);
    const roundCount = Math.floor(getNumberSetting(banner.dataset.countdownRounds, 1));
    const roundDuration = roundHours * duration.hour;
    const now = Date.now();
    const startTime = startDate.getTime();

    if (now < startTime) return { status: 'pending' };

    const currentRound = Math.floor((now - startTime) / roundDuration);
    if (currentRound >= roundCount) return { status: 'expired' };

    return { status: 'active', endDate: new Date(startTime + (currentRound + 1) * roundDuration) };
  };

  const setPart = (banner, part, value) => {
    const element = banner.querySelector(`[data-countdown-part="${part}"]`);
    if (element) element.textContent = value;
  };

  const updateA11yText = (banner, parts, hideDays) => {
    const element = banner.querySelector('[data-countdown-a11y]');
    if (!element) return;

    const heading = banner.getAttribute('aria-label') || 'Sale ends in';
    const dayText = hideDays ? '' : `${parts.days} days, `;
    element.textContent = `${heading}: ${dayText}${parts.hours} hours, ${parts.minutes} minutes, ${parts.seconds} seconds.`;
  };

  const expireBanner = (banner) => {
    banner.classList.add('is-expired');

    if (banner.dataset.countdownHideWhenExpired === 'true') {
      const countdownRow = banner.closest('.utility-bar__countdown');
      if (countdownRow) countdownRow.hidden = true;
      banner.hidden = true;
      return;
    }

    const heading = banner.querySelector('.countdown-banner__heading');
    const expiredText = banner.dataset.countdownExpiredText || 'Sale has ended.';
    if (heading) heading.textContent = expiredText;

    const a11yText = banner.querySelector('[data-countdown-a11y]');
    if (a11yText) a11yText.textContent = expiredText;
  };

  const updateBanner = (banner) => {
    const countdownState = getCountdownTarget(banner);
    const countdownRow = banner.closest('.utility-bar__countdown');

    if (countdownState.status === 'pending') {
      if (countdownRow) countdownRow.hidden = true;
      banner.hidden = true;
      banner.classList.remove('is-expired');
      return true;
    }

    if (countdownState.status !== 'active' || !countdownState.endDate) {
      expireBanner(banner);
      return false;
    }

    const endDate = countdownState.endDate;
    const remaining = endDate.getTime() - Date.now();

    if (remaining <= 0) {
      expireBanner(banner);
      return false;
    }

    banner.hidden = false;
    if (countdownRow) countdownRow.hidden = false;
    banner.classList.remove('is-expired');

    const days = Math.floor(remaining / duration.day);
    const hours = Math.floor((remaining % duration.day) / duration.hour);
    const minutes = Math.floor((remaining % duration.hour) / duration.minute);
    const seconds = Math.floor((remaining % duration.minute) / duration.second);
    const parts = {
      days: pad(days),
      hours: pad(hours),
      minutes: pad(minutes),
      seconds: pad(seconds),
    };

    // Under a day left: drop the "00 days :" unit (CSS hides it via this class).
    const hideDays = days <= 0;
    banner.classList.toggle('countdown-banner--no-days', hideDays);

    Object.entries(parts).forEach(([part, value]) => setPart(banner, part, value));
    updateA11yText(banner, parts, hideDays);

    return true;
  };

  const initBanner = (banner) => {
    if (countdownTimers.has(banner)) clearInterval(countdownTimers.get(banner));

    setRandomLink(banner);

    const isActive = updateBanner(banner);
    if (!isActive) return;

    countdownTimers.set(
      banner,
      setInterval(() => {
        if (!updateBanner(banner)) {
          clearInterval(countdownTimers.get(banner));
          countdownTimers.delete(banner);
        }
      }, duration.second)
    );
  };

  const initCountdowns = (root = document) => {
    root.querySelectorAll('[data-countdown-banner]').forEach(initBanner);
  };

  document.addEventListener('DOMContentLoaded', () => initCountdowns());
  document.addEventListener('shopify:section:load', (event) => initCountdowns(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-countdown-banner]').forEach((banner) => {
      if (!countdownTimers.has(banner)) return;
      clearInterval(countdownTimers.get(banner));
      countdownTimers.delete(banner);
    });
  });
})();
